import { isSafeRedirectPath } from '@/shared/components/layout/ProtectedRoute'

/**
 * The page half of the service worker's push deep-link handoff (IN-12).
 *
 * `frontend/public/sw.js` parks the destination of a tapped notification in the
 * Cache API before it focuses or opens a window, because neither of its two
 * direct routes is reliable on a phone: a postMessage to a frozen PWA window is
 * dropped, and `clients.openWindow()` in an iOS home-screen web app launches the
 * app without honouring the URL. The app reads the parked entry when it next
 * becomes visible, so the tap lands on the channel either way.
 *
 * Contract with sw.js — change both halves together:
 *   cache  'sf-pending-navigation'
 *   key    '/__studioflow_pending_navigation'
 *   body   {"url": "/chat/12?msg=345", "ts": 1757400000000}
 */
export const PENDING_NAV_CACHE = 'sf-pending-navigation'
export const PENDING_NAV_KEY = '/__studioflow_pending_navigation'

/**
 * How long a parked destination stays worth honouring. Long enough to survive a
 * cold PWA launch on a slow phone, short enough that a notification tapped and
 * abandoned before lunch does not yank the reader somewhere at 14:00.
 */
export const PENDING_NAV_MAX_AGE_MS = 2 * 60 * 1000

/**
 * Read and DELETE the parked destination. Returns the in-app path to navigate
 * to, or null when there is nothing pending, the entry is stale, or it does not
 * look like a same-origin app path.
 *
 * Deleted before it is validated, deliberately: one tap is one jump, and an
 * entry we refuse to honour must not sit there waiting to be honoured later.
 */
export async function consumePendingNavigation(now: number = Date.now()): Promise<string | null> {
  if (typeof caches === 'undefined') return null
  try {
    const cache = await caches.open(PENDING_NAV_CACHE)
    const response = await cache.match(PENDING_NAV_KEY)
    if (!response) return null
    await cache.delete(PENDING_NAV_KEY)

    const parked = (await response.json()) as { url?: unknown; ts?: unknown }
    // isSafeRedirectPath is the same guard the post-login redirect uses: a
    // relative app path only, never `//evil.example` and never back to /login.
    if (!isSafeRedirectPath(typeof parked.url === 'string' ? parked.url : null)) return null
    if (typeof parked.ts !== 'number' || now - parked.ts > PENDING_NAV_MAX_AGE_MS) return null
    return parked.url as string
  } catch {
    // No Cache API, a quota error, or a body we cannot parse. The push still
    // opened the app; it just does not deep-link. Never throw into a render.
    return null
  }
}
